import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Modal from '@mui/material/Modal';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { Breadcrumbs, CheckBox, FormInput, FormTextArea, ModalPrivacyTerm } from '../../components';
import { LangContext, SnackbarContext, UserContext } from '../../contexts';
import { useLabel } from '../../hooks';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  bgcolor: 'background.paper',
  border: '1px solid #bcbcbc',
  p: 0,
  boxSizing: 'border-box',
};

export const CsContactUsPage = (props) => {
  const { kakao } = window;
  const { t } = useTranslation();
  const { MSG, ERROR } = useLabel();
  const { userInfo } = useContext(UserContext);
  const { isKorean } = useContext(LangContext);
  const { updateSnackbar } = useContext(SnackbarContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
      agree: false,
    },
  });

  const validations = useMemo(
    () => ({
      name: {
        required: ERROR.NAME_BLANK,
        validate: (value) => !!value.trim() || ERROR.NAME_BLANK,
      },
      email: {
        required: ERROR.EMAIL_BLANK,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: isKorean ? '올바른 이메일 주소를 입력해주세요.' : 'Please enter a valid email address.',
        },
      },
      phone: {
        required: ERROR.PHONE_BLANK,
        pattern: {
          value: /^[0-9]{10,11}$/,
          message: isKorean
            ? '올바른 휴대폰 번호를 입력해주세요. (10-11자리)'
            : 'Please enter a valid phone number (10-11 digits).',
        },
      },
      message: {
        required: ERROR.CONTENT_BLANK,
        validate: (value) => !!value.trim() || ERROR.CONTENT_BLANK,
      },
      agree: {
        required: ERROR.AGREE_PRIVACY_TERM,
        validate: (value) => value === true || ERROR.AGREE_PRIVACY_TERM,
      },
    }),
    [ERROR, isKorean],
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (userInfo) {
      setValue('name', userInfo.name || '');
      setValue('email', userInfo.email || '');
      setValue('phone', userInfo.phone || '');
    }
  }, [userInfo]);

  useEffect(() => {
    var container = document.getElementById('map');
    var options = {
      // center: new kakao.maps.LatLng(127.10172, 37.33831),
      center: new window.kakao.maps.LatLng(37.52370646202895, 126.92689271856761),

      level: 3,
    };

    var map = new kakao.maps.Map(container, options);

    //마커가 표시 될 위치
    let markerPosition = new window.kakao.maps.LatLng(37.52370646202895, 126.92689271856761);

    // 마커를 생성
    let marker = new kakao.maps.Marker({
      position: markerPosition,
    });

    // 마커를 지도 위에 표시
    marker.setMap(map);

    // 인포윈도우로 장소에 대한 설명을 표시합니다
    var infowindow = new kakao.maps.InfoWindow({
      content:
        '<div style="width:180px;text-align:center;padding:20px 0;font-size : 12px;">' +
        '<p style="font-size: 14px; line-height:1.5; padding: 0 10px; word-break: keep-all;">' +
        t('COMPANY_ADDRESS') +
        '</p>' +
        '</div>',
    });
    infowindow.open(map, marker);
  }, [kakao, isKorean]);

  const onSubmit = (data) => {
    // agree는 클라이언트 검증용이므로 서버로 보내지 않음
    const { agree, ...submitData } = data;

    API.CS.PostContact(submitData).then(
      (res) => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: MSG.INQUIRY_SUBMIT_SUCCESS,
        });
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  return (
    <article className='basic-page'>
      <article className='content__wrapper'>
        <section className='headding-text__container'>
          <header
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <h1>Contact us</h1>
          </header>
          <div className='right__box'>
            <Breadcrumbs type={'small'}></Breadcrumbs>
          </div>
        </section>
        <section className='contact-us-form__container'>
          <form id='contact-form' noValidate onSubmit={handleSubmit(onSubmit)}>
            <ul className='form__box'>
              <div className='guide-phone__box'>
                <header>
                  <h3 style={{ marginRight: 17 }}>{t('INQUIRY')}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h3>02-6274-3600</h3>
                    {/* <h3 style={{ marginTop: 4 }}>031-548-2307</h3> */}
                  </div>
                </header>
                <div className='time__box'>
                  <div className='item'>
                    <header>
                      <div className='ico next'></div>
                      <span>{t('WEEKDAYS')}</span>
                    </header>
                    <span>09:00 ~ 18:00</span>
                  </div>
                  <div className='item'>
                    <header>
                      <div className='ico next'></div> <span>{t('LUNCH')}</span>
                    </header>
                    <span>12:00 ~ 13:00</span>
                  </div>
                </div>
                <div className='illust'></div>
              </div>
              <div className='guide-text__box'>
                <p className='leading-6 text-sm break-keep mx-2'>{t('CONTACT_US_GUIDE')}</p>
              </div>
              <li className='mb-4'>
                <FormInput
                  label={t('NAME')}
                  required
                  placeholder={t('NAME')}
                  error={errors.name}
                  {...register('name', {
                    required: validations.name.required,
                    validate: validations.name.validate,
                  })}
                />
              </li>
              <li className='mb-4'>
                <FormInput
                  label={t('EMAIL')}
                  required
                  placeholder={t('EMAIL')}
                  error={errors.email}
                  {...register('email', {
                    required: validations.email.required,
                    pattern: validations.email.pattern,
                  })}
                />
              </li>
              <li className='mb-4'>
                <FormInput
                  label={t('MOBILE')}
                  required
                  placeholder={t('MOBILE')}
                  error={errors.phone}
                  {...register('phone', {
                    required: validations.phone.required,
                    pattern: validations.phone.pattern,
                    onChange: (e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      e.target.value = numericValue.slice(0, 11);
                    },
                  })}
                />
              </li>
              <li className='mb-4'>
                <FormTextArea
                  label={t('CONTENT')}
                  required
                  rows={12}
                  placeholder={t('CONTENT')}
                  maxLength={500}
                  error={errors.message}
                  {...register('message', {
                    required: validations.message.required,
                    validate: validations.message.validate,
                  })}
                />
              </li>
              <div className='aggree__box !mb-0'>
                <Controller
                  name='agree'
                  control={control}
                  rules={{
                    required: validations.agree.required,
                    validate: validations.agree.validate,
                  }}
                  render={({ field }) => (
                    <CheckBox
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      meta={{ label: MSG['AGREE_PRIVACY_TERM'] }}
                    />
                  )}
                />
                <div className='option__box'>
                  <span
                    onClick={() => {
                      setOpen(true);
                    }}
                  >
                    {t('SHOW')}
                  </span>
                </div>
              </div>
              {errors.agree && <FormHelperText error>{errors.agree.message}</FormHelperText>}
              <section className='button__box'>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  form='contact-form'
                  fullWidth
                  className='h-[56px] !rounded-lg !mt-4'
                >
                  {t('INQUIRY')}
                </Button>
              </section>
            </ul>
          </form>
          <div className='map__box'>
            <div className='daum-map__box'>
              <div id='map' style={{ width: '665px', height: '665px' }}></div>
            </div>
            <div className='text__box'>
              <span>07327</span>
              <span>{t('COMPANY_ADDRESS')}</span>
              <span>TEL: 02-6274-3600 FAX: 02-6221-0801</span>
              <span>
                <a className='text-primary underline' href='mailto:info@greenery.im'>
                  info@greenery.im
                </a>
              </span>
            </div>
          </div>
        </section>
      </article>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box sx={style}>
          <article className='contact-privacy__modal'>
            <ModalPrivacyTerm
              onClose={() => {
                setOpen(false);
              }}
            />
          </article>
        </Box>
      </Modal>
    </article>
  );
};

